import React, { useEffect, useState, useMemo } from 'react';

const API_BASE_URL = 'https://dolphin-app-33jp4.ondigitalocean.app';

// ── Stable star fields (computed once to avoid re-render flicker) ──────────
function useStars(count) {
  return useMemo(() => Array.from({ length: count }, (_, i) => ({
    id: i,
    width:  (Math.random() * 2 + 1).toFixed(2),
    height: (Math.random() * 2 + 1).toFixed(2),
    top:    (Math.random() * 100).toFixed(2),
    left:   (Math.random() * 100).toFixed(2),
  })), [count]);
}

function Stars({ count = 80, opacity = 0.5, color = 'bg-white' }) {
  const stars = useStars(count);
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {stars.map(s => (
        <div
          key={s.id}
          className={`absolute ${color} rounded-full`}
          style={{ width: s.width + 'px', height: s.height + 'px', top: s.top + '%', left: s.left + '%', opacity }}
        />
      ))}
    </div>
  );
}

// ── Chevron-shaped icon box used in Essential Slides ──────────────────────
function ChevronBox({ icon }) {
  return (
    <div
      className="flex-shrink-0 w-14 h-14 sm:w-20 sm:h-20 bg-orange-800/70 flex items-center justify-center"
      style={{ clipPath: 'polygon(0 0, 82% 0, 100% 50%, 82% 100%, 0 100%)' }}
    >
      <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        {icon}
      </svg>
    </div>
  );
}

// ── Section wrapper with stars background ────────────────────────────────
function Section({ children, className = '', stars = true, starOpacity = 0.4 }) {
  return (
    <section className={`relative ${className}`}>
      {stars && <Stars count={80} opacity={starOpacity} />}
      {children}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
export default function Pitchdeck() {
  const [hasRefAccess, setHasRefAccess] = useState(false);

  // ── Ref-access logic ────────────────────────────────────────────────────
  useEffect(() => {
    const checkRefAccess = () => {
      try {
        const user = JSON.parse(localStorage.getItem('userData') || 'null');
        if (user && (user.role === 'super_admin' || user.email === 'info@easylearn.net.in')) {
          setHasRefAccess(true);
        } else {
          setHasRefAccess(localStorage.getItem('refAccess') === '1');
        }
      } catch { setHasRefAccess(false); }
    };

    checkRefAccess();

    const poll = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('userData') || 'null');
        if (!user?.email || user.role === 'super_admin' || user.email === 'info@easylearn.net.in') return;
        const res  = await fetch(`${API_BASE_URL}/isfathena/user/can-access-reference?email=${encodeURIComponent(user.email)}`);
        const data = await res.json();
        const newVal = data.can_access ? '1' : '0';
        if (newVal !== localStorage.getItem('refAccess')) {
          localStorage.setItem('refAccess', newVal);
          setHasRefAccess(data.can_access);
        }
      } catch {}
    };

    const onStorage = (e) => {
      if (e.key === 'refAccessToggle') {
        try {
          const { email, value } = JSON.parse(e.newValue);
          const user = JSON.parse(localStorage.getItem('userData') || 'null');
          if (user?.email === email) {
            localStorage.setItem('refAccess', value);
            setHasRefAccess(value === '1');
          }
        } catch {}
      }
      checkRefAccess();
    };

    const interval = setInterval(poll, 10000);
    window.addEventListener('storage', onStorage);
    window.addEventListener('refAccessChange', checkRefAccess);
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('refAccessChange', checkRefAccess);
    };
  }, []);

  // ── Content protection ──────────────────────────────────────────────────
  useEffect(() => {
    const noop = (e) => e.preventDefault();
    const keydown = (e) => {
      if (e.key === 'F12' || (e.ctrlKey && e.shiftKey && ['I', 'J'].includes(e.key)) || (e.ctrlKey && e.key === 'U'))
        e.preventDefault();
    };
    document.addEventListener('contextmenu', noop);
    document.addEventListener('keydown', keydown);
    document.addEventListener('copy', noop);
    document.addEventListener('dragstart', noop);
    document.body.style.userSelect = 'none';
    return () => {
      document.removeEventListener('contextmenu', noop);
      document.removeEventListener('keydown', keydown);
      document.removeEventListener('copy', noop);
      document.removeEventListener('dragstart', noop);
      document.body.style.userSelect = '';
    };
  }, []);

  // Navigate helper (works standalone; swap for useNavigate in full app)
  const navigate = (path) => { window.location.href = path; };

  return (
    <div className="bg-black text-white min-h-screen overflow-x-hidden w-full">

      <style>{`
        img { pointer-events: none; user-select: none; -webkit-user-drag: none; }
        html, body { overflow-x: hidden; max-width: 100%; }
        * { box-sizing: border-box; }
      `}</style>

      {/* Global star backdrop */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <Stars count={80} opacity={0.5} />
      </div>

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative px-4 sm:px-8 lg:px-24 bg-black pt-16 sm:pt-20 z-10">
        <div className="max-w-6xl mx-auto relative z-10 w-full">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-center">

            {/* Text */}
            <div className="w-full lg:w-1/2 flex flex-col py-10 sm:py-16 space-y-6 text-left">
              <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
                Pitchdeck Investor Pitch Documentation System: Your Ultimate Fundraising Companion
              </h1>
              <p className="text-gray-300 text-base lg:text-lg leading-relaxed max-w-xl">
                Craft compelling investor decks, track engagement in real-time, and close funding rounds faster — all powered by AI and proven storytelling frameworks.
              </p>
              <div className="flex flex-wrap gap-3 sm:gap-4">
                <button className="px-6 sm:px-8 py-3 bg-orange-500 text-white rounded-md font-semibold hover:bg-orange-600 transition">
                  Start Free Trial
                </button>
                <button
                //   onClick={() => navigate('/reffrence?productId=5')}
                  className="px-8 py-3 border-2 border-white text-white rounded-md font-semibold hover:bg-white hover:text-black transition">
                  Learn More
                </button>
              </div>
            </div>

            {/* Hero image */}
            <div className="w-full lg:w-1/2 flex justify-center">
              <img
                src="/Assests/home_page_195.png"
                alt="Pitchdeck Hero"
                className="w-full max-w-lg object-cover object-center rounded-2xl"
                style={{ maxHeight: '520px' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── CHAPTER 1: WHY IT MATTERS ────────────────────────────────────── */}
      <Section className="px-4 sm:px-8 lg:px-24 py-16 sm:py-24 bg-black z-10">
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
            Why Your Pitch Deck Matters More Than Ever
          </h2>
          <p className="text-gray-300 text-base lg:text-lg leading-relaxed mb-14 max-w-5xl mx-auto">
            In 2026's hyper-competitive funding landscape, your pitch deck is far more than a slide show — it's your startup's first handshake with investors. With hundreds of decks flooding investor inboxes weekly, standing out requires precision, storytelling, and data.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
            {[
              { value: '3:44', label: 'Minutes Reviewed',   note: 'Average time investors spend on a pitch deck (DocSend, 2026)' },
              { value: '31%',  label: 'Quit in 10 Sec',     note: 'Nearly a third of investors abandon decks within seconds' },
              { value: '100s', label: 'Decks Per Week',     note: 'Investors review hundreds of decks, making differentiation critical' },
            ].map((s, i) => (
              <div key={i} className="space-y-2">
                <p className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white">{s.value}</p>
                <h3 className="text-base font-bold text-white">{s.label}</h3>
                <p className="text-gray-400 text-sm leading-snug max-w-xs mx-auto">{s.note}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── THE CHALLENGE ────────────────────────────────────────────────── */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-16 bg-[#b5601e] z-10">
        <div className="max-w-6xl mx-auto text-center">
          <div className="bg-[#1a0e05] rounded-2xl p-6 sm:p-10 lg:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="space-y-5 text-left">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight">
                  The Challenge: Founders Struggle to Get Investor Attention
                </h2>
                <p className="text-gray-300 text-base leading-relaxed">
                  Over <strong className="text-white">700+ pitch decks</strong> reviewed by fundraising experts reveal a consistent truth: most fail due to lack of compelling storytelling and unclear messaging.
                </p>
                <div className="space-y-3">
                  <h3 className="text-lg font-bold text-white">Common Pitfalls That Kill Deals</h3>
                  {[
                    'Cluttered slides with too much information',
                    "Unclear problem statements that don't resonate",
                    'Weak traction evidence and inflated projections',
                    'Poor visual design that signals inexperience',
                  ].map((p, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="text-orange-400 mt-1 flex-shrink-0">•</span>
                      <p className="text-gray-300 text-base">{p}</p>
                    </div>
                  ))}
                </div>
                <p className="text-gray-300 text-base leading-relaxed">
                  The fundraising landscape in 2026 is more competitive and fast-paced than ever before.
                </p>
              </div>
              <div className="rounded-xl overflow-hidden">
                <img src="/Assests/home_page_196.png" alt="Founder struggling with pitch deck" className="w-full h-full object-cover rounded-xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CHAPTER 2: INTRODUCING THE SYSTEM ───────────────────────────── */}
      <Section className="px-4 sm:px-8 lg:px-24 py-16 sm:py-20 bg-black z-10" starOpacity={0.5}>
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 sm:mb-8 leading-tight">
            Introducing the Pitchdeck Investor Pitch Documentation System
          </h2>
          <p className="text-gray-300 text-base lg:text-xl leading-relaxed mb-12 max-w-5xl mx-auto">
            A comprehensive platform designed to help founders <strong className="text-white">craft, track, and optimize</strong> investor pitch decks with confidence.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              { title: 'AI-Powered Feedback',   desc: 'Instant, actionable insights on every slide',       d: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' },
              { title: 'Engagement Analytics', desc: 'Track investor behavior in real-time',               d: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
              { title: 'Storytelling Frameworks', desc: 'Proven blueprints from funded startups',          d: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' },
              { title: 'Seamless Workflow',     desc: 'Integrates with your fundraising pipeline',        d: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z' },
            ].map((item, i) => (
              <div key={i} className="border-2 border-orange-500 rounded-2xl p-6 sm:p-8 bg-black/50 text-left">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-orange-500 rounded-full flex items-center justify-center mb-4 sm:mb-6">
                  <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.d} />
                  </svg>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── CORE FEATURES OVERVIEW ───────────────────────────────────────── */}
      <Section className="px-4 sm:px-8 lg:px-24 py-16 bg-black z-10">
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">Core Features Overview</h2>
          <p className="text-gray-300 text-base lg:text-lg leading-relaxed mb-10 max-w-3xl mx-auto">
            Everything you need to create, refine, and deliver a winning pitch — unified in one powerful platform.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border border-gray-700">
            {[
              { img: '/Assests/home_page_197.png', title: 'AI-Driven Pitch Deck Review',       desc: 'Instant, actionable feedback on market validation, financials, and slide clarity to sharpen your narrative.' },
              { img: '/Assests/home_page_198.png', title: 'Investor Engagement Tracking',     desc: 'See who viewed your deck, which slides captivated them, and forwarding activity — powered by DocSend technology.' },
              { img: '/Assests/home_page_199.png', title: 'Storytelling Frameworks',          desc: 'Proven slide-by-slide blueprints modeled after decks that secured millions in funding.' },
              { img: '/Assests/home_page_200.png', title: 'Collaborative Editing',            desc: 'Work with your team or advisors to iterate quickly with version control and real-time co-editing.' },
            ].map((item, i) => (
              <div key={i} className="border border-gray-700 flex flex-col">
                <div className="w-full aspect-video overflow-hidden">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-4 sm:p-5 flex flex-col gap-2">
                  <h3 className="text-sm sm:text-base font-bold text-orange-400 leading-snug">{item.title}</h3>
                  <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── AI-POWERED FEEDBACK TOOLS ────────────────────────────────────── */}
      <Section className="px-4 sm:px-8 lg:px-24 py-16 bg-black z-10">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <div className="space-y-6">
              <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
                AI-Powered Feedback Tools That Transform Your Deck
              </h2>
              <p className="text-gray-300 text-base lg:text-lg leading-relaxed">
                Leverage the best AI tools in the fundraising ecosystem — each designed to elevate a different dimension of your pitch.
              </p>
              <div className="space-y-4">
                {[
                  { num: 1, title: 'Peachscore',     desc: 'Tailors feedback to investor expectations, highlighting gaps in market research and financial modeling with precision.' },
                  { num: 2, title: 'Decktopus',      desc: 'Provides real-time guidance on structure and visual design to keep your narrative clear and compelling throughout.' },
                  { num: 3, title: 'Beautiful.ai',   desc: 'Automates slide layout and branding for professional polish — no design expertise required.' },
                  { num: 4, title: 'PitchLeague.ai', desc: 'Analyzes storytelling impact and investor psychology to maximize emotional engagement and memorability.' },
                ].map(t => (
                  <div key={t.num} className="flex border border-orange-500 rounded-xl overflow-hidden">
                    <div className="border-r border-orange-500 flex items-center justify-center px-4 sm:px-5 text-white font-bold text-lg sm:text-xl min-w-[48px] sm:min-w-[56px]">
                      {t.num}
                    </div>
                    <div className="p-4 sm:p-5 space-y-1">
                      <h3 className="text-white font-bold text-sm sm:text-base">{t.title}</h3>
                      <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">{t.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="w-full">
              <img src="/Assests/home_page_201.png" alt="AI Feedback Tools" className="w-full object-cover rounded-xl" style={{ minHeight: '400px', maxHeight: '600px' }} />
            </div>
          </div>
        </div>
      </Section>

      {/* ── CHAPTER 3: INVESTOR ENGAGEMENT ───────────────────────────────── */}
      <Section className="px-4 sm:px-8 lg:px-24 py-16 sm:py-20 bg-black z-10" starOpacity={0.5}>
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 sm:mb-8 leading-tight">
            Tracking Investor Engagement — Know Your Deck's Impact
          </h2>
          <p className="text-gray-300 text-base lg:text-xl leading-relaxed mb-12 sm:mb-16 max-w-5xl mx-auto">
            Stop guessing and start knowing. DocSend-powered analytics reveal exactly how investors interact with your deck.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 sm:gap-12">
            {[
              { value: '40%', title: 'More Meetings',     desc: 'Startups using engagement tracking increased investor meetings in 2025', offset: 150 },
              { value: '3/4', title: 'Refined Messaging', desc: 'Of users improved their pitch after reviewing slide-level analytics',     offset: 125 },
              { value: '60%', title: 'Faster Follow-ups', desc: 'Reduction in response time by prioritizing engaged investors',            offset: 200 },
            ].map((s, i) => (
              <div key={i} className="flex flex-col items-center text-center space-y-4">
                <div className="relative w-40 h-40 sm:w-48 sm:h-48">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle cx="50%" cy="50%" r="42%" stroke="#d97706" strokeWidth="6%" fill="none" opacity="0.3" />
                    <circle cx="50%" cy="50%" r="42%" stroke="#ea580c" strokeWidth="6%" fill="none" strokeDasharray="502" strokeDashoffset={s.offset} strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-3xl sm:text-4xl font-bold text-white">{s.value}</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-2">{s.title}</h3>
                  <p className="text-gray-300 text-sm max-w-xs mx-auto">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── COMPELLING NARRATIVE ─────────────────────────────────────────── */}
      <Section className="px-4 sm:px-8 lg:px-24 py-16 bg-black z-10">
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-10 leading-tight">
            Building a Compelling Narrative: The Story Investors Remember
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-start text-left">
            <div className="bg-[#b5601e] rounded-2xl p-6 sm:p-8 space-y-5 h-full">
              <h3 className="text-lg font-bold text-white">The Golden Rule</h3>
              <p className="text-white text-sm leading-relaxed">
                Investors invest in being <strong>compelled</strong>, not convinced. Storytelling is your secret weapon — the difference between a polite pass and a term sheet.
              </p>
              <div className="border-l-2 border-white/60 pl-4">
                <p className="text-white text-sm italic">"The best pitch decks read like page-turners, not textbooks."</p>
              </div>
            </div>
            <div className="space-y-0">
              <h3 className="text-lg font-bold text-white mb-4">The Winning Framework</h3>
              {[
                { num: '01', title: 'Problem Clarity',       desc: 'Define the pain with razor-sharp precision' },
                { num: '02', title: 'Solution Uniqueness',   desc: 'Show what makes your offering irreplaceable' },
                { num: '03', title: 'Market Opportunity',    desc: 'Quantify the massive upside' },
                { num: '04', title: 'Traction Proof',        desc: 'Lead with your strongest growth signals' },
                { num: '05', title: 'Competitive Advantage', desc: 'Articulate your defensible moat' },
                { num: '06', title: 'Funding Ask',           desc: 'Be specific about needs and milestones' },
              ].map((item, i) => (
                <div key={i}>
                  <p className="text-gray-500 text-xs mt-4 mb-1">{item.num}</p>
                  <div className="border-t border-orange-500/60 pt-2 pb-1">
                    <h4 className="text-white font-bold text-sm sm:text-base mb-1">{item.title}</h4>
                    <p className="text-gray-300 text-xs sm:text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* ── CHAPTER 4: ESSENTIAL SLIDES ──────────────────────────────────── */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-16 bg-[#b5601e] z-10">
        <Stars count={60} opacity={0.2} color="bg-orange-200" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
            Essential Slides Every Investor Wants to See
          </h2>
          <p className="text-white/80 text-sm lg:text-base leading-relaxed mb-8 max-w-3xl mx-auto">
            Your deck should tell a complete story in a logical sequence.
          </p>
          <div className="border border-orange-400/40 rounded overflow-hidden">
            {[
              { title: 'Problem & Opportunity', desc: 'Define the pain point and market size with crisp data',             d: 'M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z' },
              { title: 'Solution & Product',    desc: 'Show how your offering uniquely solves the problem',                d: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' },
              { title: 'Traction & Metrics',    desc: 'Lead with your strongest growth indicators early',                   d: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6' },
              { title: 'Business Model',        desc: 'Ground projections in bottom-up assumptions',                       d: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
              { title: 'Team & Vision',         desc: 'Highlight execution capability and long-term potential',            d: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z' },
              { title: 'Ask & Use of Funds',    desc: 'Be clear and specific about milestones',                           d: 'M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' },
            ].map((slide, i, arr) => (
              <div key={i} className={`flex items-center ${i !== arr.length - 1 ? 'border-b border-orange-400/40' : ''}`}>
                <ChevronBox icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d={slide.d} />} />
                <div className="px-4 sm:px-6 py-3 sm:py-4 text-left">
                  <h3 className="text-white font-bold text-sm sm:text-base mb-0.5">{slide.title}</h3>
                  <p className="text-white/70 text-xs sm:text-sm">{slide.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── DESIGN PRINCIPLES ────────────────────────────────────────────── */}
      <Section className="px-4 sm:px-8 lg:px-24 py-16 bg-black z-10">
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-10 leading-tight">
            Design Principles That Signal Professional Execution
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 border border-gray-700 rounded-xl overflow-hidden">
            <div className="w-full">
              <img src="/Assests/home_page_202.png" alt="Design Principles" className="w-full h-full object-cover" style={{ minHeight: '260px', maxHeight: '500px' }} />
            </div>
            <div className="border-t lg:border-t-0 lg:border-l border-gray-700 divide-y divide-gray-700">
              {[
                { title: 'Ruthless Simplicity',   desc: 'One message per slide, minimal text, and clear visuals that let your story breathe.' },
                { title: 'Consistent Formatting', desc: 'Quality charts and uniform styling build investor confidence in your attention to detail.' },
                { title: 'Visual Hierarchy',      desc: 'Guide investor eyes to key insights instantly through contrast, size, and placement.' },
                { title: 'Brand Identity',        desc: 'Reinforce credibility with consistent brand colors, fonts, and design language throughout.' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 px-5 py-4 sm:px-6 sm:py-5 text-left">
                  <div className="w-2.5 h-2.5 bg-orange-500 rounded-full mt-1.5 flex-shrink-0"></div>
                  <div>
                    <h3 className="text-white font-bold text-sm sm:text-base mb-1">{item.title}</h3>
                    <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* ── CHAPTER 5: COLLABORATION ─────────────────────────────────────── */}
      <Section className="px-4 sm:px-8 lg:px-24 py-16 sm:py-20 bg-black z-10" starOpacity={0.5}>
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 sm:mb-8 leading-tight">
            Seamless Collaboration and Version Control
          </h2>
          <p className="text-gray-300 text-base lg:text-xl leading-relaxed mb-10 sm:mb-12 max-w-5xl mx-auto">
            Great pitch decks are never built alone. Invite co-founders, advisors, and designers to collaborate in real-time while maintaining a single source of truth.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {[
              { title: 'Real-Time Co-Editing', desc: 'Multiple team members can work simultaneously without conflicts or confusion.',                                                    d: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' },
              { title: 'Version Archive',      desc: 'Track changes and archive previous versions to analyze what resonated best with investors.',                                       d: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
              { title: 'Multi-Format Export',  desc: 'Optimized exports for email, virtual meetings, or in-person presentations — always pitch-ready.',                               d: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4' },
            ].map((item, i) => (
              <div key={i} className="space-y-4 text-left">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-orange-500/20 border-2 border-orange-500 rounded-2xl flex items-center justify-center">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.d} />
                  </svg>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white">{item.title}</h3>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── INTEGRATIONS ─────────────────────────────────────────────────── */}
      <Section className="px-4 sm:px-8 lg:px-24 py-16 bg-black z-10">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <div className="space-y-6">
              <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
                Integrations and Workflow Automation
              </h2>
              <p className="text-gray-300 text-base lg:text-lg leading-relaxed">
                Streamline your entire fundraising pipeline by connecting Pitchdeck with the tools you already use.
              </p>
              <div className="space-y-4">
                {[
                  { title: 'CRM & Fundraising Platforms', desc: 'Connect with popular tools to streamline investor outreach and relationship management seamlessly.' },
                  { title: 'Automated Follow-Ups',        desc: 'Smart reminders and email templates triggered by investor engagement data ensure no opportunity slips away.' },
                  { title: 'Secure Data Rooms',           desc: 'Share confidential documents alongside your pitch deck with investor-grade encryption and access controls.' },
                  { title: 'API & Custom Integrations',   desc: 'Advanced API access for custom workflows and deeper analytics tailored to your fundraising process.' },
                ].map((item, i) => (
                  <div key={i} className="border border-orange-500 rounded-xl p-4 sm:p-5 bg-black">
                    <h3 className="text-white font-bold text-sm sm:text-base mb-2">{item.title}</h3>
                    <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="w-full">
              <img src="/Assests/home_page_203.png" alt="Integrations" className="w-full object-cover rounded-xl" style={{ minHeight: '400px', maxHeight: '620px' }} />
            </div>
          </div>
        </div>
      </Section>

      {/* ── CHAPTER 6: SUCCESS STORIES ───────────────────────────────────── */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-16 overflow-hidden z-10">
        <div className="absolute inset-0">
          <img src="/Assests/home_page_204.png" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="max-w-6xl mx-auto relative z-10">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-10 leading-tight max-w-3xl">
            Real Success Stories Powered by the System
          </h2>
          {/* Cards - responsive stacked on mobile, offset on lg */}
          <div className="flex flex-col sm:flex-row gap-6 mb-8 relative">
            <div className="border border-orange-400 rounded-xl p-5 sm:p-6 bg-black/70 w-full sm:max-w-sm">
              <h3 className="text-white font-bold text-base sm:text-lg mb-3">Startup X: $5M Series A</h3>
              <p className="text-white/80 text-sm leading-relaxed">
                Used AI feedback to refine their deck and engagement tracking to prioritize investor meetings — closing their round in record time.
              </p>
            </div>
            <div className="border border-gray-500 rounded-xl p-5 sm:p-6 bg-black/80 w-full sm:max-w-sm sm:mt-16">
              <h3 className="text-white font-bold text-base sm:text-lg mb-3">Founder Y: 50% Faster Fundraise</h3>
              <p className="text-white/80 text-sm leading-relaxed">
                Leveraged storytelling frameworks and collaboration tools to cut fundraising time in half while increasing investor interest.
              </p>
            </div>
          </div>
          <div className="bg-orange-900/80 border border-orange-700 rounded-xl p-4 sm:p-5 flex items-start gap-4 max-w-3xl">
            <div className="w-4 h-4 border border-orange-400 rounded-sm mt-0.5 flex-shrink-0"></div>
            <p className="text-white text-sm leading-relaxed">
              These case studies demonstrate measurable improvements in investor interest, meeting conversion rates, and overall funding velocity across different stages and industries.
            </p>
          </div>
        </div>
      </section>

      {/* ── PRICING ──────────────────────────────────────────────────────── */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-20 bg-[#b5601e] z-10">
        <Stars count={60} opacity={0.2} color="bg-orange-200" />
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Pricing and Plans Tailored for Every Stage
          </h2>
          <p className="text-orange-200 text-base lg:text-lg leading-relaxed mb-10 sm:mb-12">
            Whether you're pre-seed or scaling to enterprise, there's a plan built for your fundraising journey.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { name: 'Starter',    sub: 'Free Trial',             features: ['Core AI feedback tools', 'Basic engagement tracking', 'Proven templates library'] },
              { name: 'Growth',     sub: 'For Scaling Startups',   features: ['Advanced analytics dashboard', 'Unlimited collaborators', 'CRM integrations'] },
              { name: 'Enterprise', sub: 'Fundraising Teams',      features: ['Personalized coaching sessions', 'Custom deck builds', 'Priority support & API access'] },
            ].map((plan, i) => (
              <div key={i} className="bg-black rounded-2xl p-6 sm:p-8 border-2 border-orange-500 space-y-5 text-left">
                <h3 className="text-xl sm:text-2xl font-bold text-white">{plan.name}</h3>
                <p className="text-orange-400 font-bold text-base sm:text-lg">{plan.sub}</p>
                <div className="space-y-3">
                  {plan.features.map((f, j) => (
                    <div key={j} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                      <p className="text-gray-300 text-sm sm:text-base">{f}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CHAPTER 7: GETTING STARTED ───────────────────────────────────── */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-16 bg-[#b5601e] z-10">
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="bg-[#1a0e05] rounded-2xl p-6 sm:p-10 lg:p-12">
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
              Getting Started — Your Fundraising Journey Simplified
            </h2>
            <div className="flex items-start gap-3 mb-8 sm:mb-10 max-w-2xl mx-auto text-left">
              <div className="w-4 h-4 bg-blue-600 rounded-sm flex-shrink-0 mt-1"></div>
              <p className="text-white/80 text-sm leading-relaxed">
                From first upload to investor meeting, the entire process is designed to be intuitive and fast. Here's how it works:
              </p>
            </div>
            {/* Steps – scrollable on mobile, flex row on larger screens */}
            <div className="flex flex-wrap sm:flex-nowrap items-center justify-center gap-2 sm:gap-0 mb-8">
              {[
                { label: 'Upload Deck', d: 'M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12' },
                { label: 'AI Improve',  d: 'M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z' },
                { label: 'Share & Track', d: 'M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z' },
                { label: 'Iterate',    d: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
              ].map((step, i, arr) => (
                <div key={i} className="flex items-center">
                  <div className="flex flex-col items-center gap-2 sm:gap-3">
                    <div className="w-20 h-20 sm:w-28 sm:h-28 rounded-full border-4 border-orange-500 bg-[#1a0e05] flex items-center justify-center">
                      <svg className="w-8 h-8 sm:w-10 sm:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d={step.d} />
                      </svg>
                    </div>
                    <p className="text-white font-bold text-xs sm:text-sm text-center">{step.label}</p>
                  </div>
                  {i < arr.length - 1 && (
                    <div className="flex-shrink-0 mx-1 sm:mx-2">
                      <svg className="w-6 h-6 sm:w-8 sm:h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5l8 7-8 7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <p className="text-white/80 text-sm text-center">
              Most founders complete their first optimized deck in <strong className="text-white">under 24 hours</strong> — and start tracking investor engagement immediately.
            </p>
          </div>
        </div>
      </section>

      {/* ── EXPERT RESOURCES ─────────────────────────────────────────────── */}
      <Section className="px-4 sm:px-8 lg:px-24 py-16 bg-black z-10">
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-10 leading-tight">
            Expert Resources and Continuous Learning
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            <div className="rounded-xl overflow-hidden">
              <img src="/Assests/home_page_205.png" alt="Expert Resources" className="w-full object-cover rounded-xl" style={{ minHeight: '280px' }} />
            </div>
            <div className="space-y-6 text-left">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pb-6 border-b border-gray-700">
                {[
                  { title: 'Knowledge Base',       desc: 'Access best practices on pitch deck design, investor psychology, and fundraising strategy — updated continuously.' },
                  { title: 'Webinars & Workshops', desc: 'Learn from seasoned VCs and successful founders through live and on-demand sessions.' },
                ].map((item, i) => (
                  <div key={i} className="border-t-2 border-orange-500 pt-3">
                    <h3 className="text-white font-bold text-sm sm:text-base mb-2">{item.title}</h3>
                    <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
              <div className="border-t-2 border-orange-500 pt-3">
                <h3 className="text-white font-bold text-sm sm:text-base mb-2">Community Forums</h3>
                <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">
                  Share experiences, get peer feedback, and connect with fellow founders on the same journey.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ── WHY CHOOSE PITCHDECK ─────────────────────────────────────────── */}
      <Section className="px-4 sm:px-8 lg:px-24 py-16 sm:py-20 bg-black z-10" starOpacity={0.5}>
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-10 sm:mb-16 leading-tight">
            Why Choose Pitchdeck Investor Pitch Documentation System?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
            {[
              { title: 'Cutting-Edge AI',      desc: 'Deep investor insights combined with advanced AI analysis' },
              { title: 'Storytelling Mastery', desc: 'Empowers founders to tell unforgettable stories that open doors' },
              { title: 'Trusted Worldwide',    desc: 'Relied upon by hundreds of startups and investors globally' },
              { title: 'Data-Driven',          desc: 'Reduces fundraising guesswork and accelerates success' },
            ].map((item, i) => (
              <div key={i} className="border-2 border-orange-500 rounded-2xl p-6 sm:p-8 bg-black/50 text-left">
                <h3 className="text-lg sm:text-xl font-bold text-white mb-4">{item.title}</h3>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <Section className="px-4 sm:px-8 lg:px-24 py-16 bg-black z-10">
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-8 leading-tight">
            Frequently Asked Questions
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {[
              { q: 'How does AI feedback improve my deck?', a: 'Our AI analyzes slide structure, financial clarity, market validation, and storytelling impact — then delivers specific, actionable recommendations in seconds.' },
              { q: 'Can I track engagement anonymously?',   a: "Yes. Investors view your deck without knowing they're being tracked. You see analytics; they see a seamless presentation experience." },
              { q: "What if I don't have a deck yet?",      a: 'No problem. Start from one of our proven templates built from decks that secured millions in real funding rounds.' },
              { q: 'How secure is my data?',                a: 'Enterprise-grade encryption, SOC 2 compliance, and granular access controls protect your confidential information at every step.' },
            ].map((faq, i) => (
              <div key={i} className="border border-orange-500 rounded-2xl p-5 sm:p-6 bg-black space-y-3 text-left">
                <h3 className="text-white font-bold text-sm sm:text-base leading-snug">{faq.q}</h3>
                <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
          <div className="border border-orange-500 rounded-2xl p-5 sm:p-6 bg-black text-left">
            <h3 className="text-white font-bold text-sm sm:text-base mb-2">What support is available?</h3>
            <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">
              From self-serve knowledge base to personalized coaching with fundraising experts — support scales with your plan.
            </p>
          </div>
        </div>
      </Section>

      {/* ── CHAPTER 8: THE FUTURE ────────────────────────────────────────── */}
      <Section className="px-4 sm:px-8 lg:px-24 py-16 bg-black z-10">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="rounded-xl overflow-hidden order-2 lg:order-1">
              <img src="/Assests/home_page_205.png" alt="The Future of Fundraising" className="w-full object-cover rounded-xl" style={{ minHeight: '340px', maxHeight: '520px' }} />
            </div>
            <div className="space-y-6 text-left order-1 lg:order-2">
              <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
                The Future of Fundraising is Here
              </h2>
              <p className="text-gray-300 text-sm lg:text-base leading-relaxed">
                AI and analytics are fundamentally reshaping how startups connect with investors. Personalized, data-driven storytelling is the new standard for winning decks — and the founders who embrace it will lead the next generation of funded companies.
              </p>
              <div className="border-l-4 border-orange-500 pl-5 py-1">
                <p className="text-gray-200 text-sm leading-relaxed">
                  Pitchdeck.co.in is committed to evolving with founders' needs and market trends. Join the revolution and fundraise <strong className="text-white">smarter, faster, and with confidence</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ── READY TO TRANSFORM ───────────────────────────────────────────── */}
      <Section className="px-4 sm:px-8 lg:px-24 py-16 bg-black z-10">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="space-y-6 text-left">
              <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
                Ready to Transform Your Fundraising?
              </h2>
              <p className="text-gray-300 text-sm lg:text-base leading-relaxed">
                Build pitch decks that get meetings, track investor interest with precision, and close deals with confidence. Your next investor meeting is just a click away.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { title: 'Build', desc: 'Craft decks with AI and proven frameworks' },
                  { title: 'Track', desc: 'Monitor investor engagement in real-time' },
                ].map((item, i) => (
                  <div key={i} className="border border-orange-500 rounded-xl p-4 sm:p-5 bg-black space-y-2">
                    <h3 className="text-white font-bold text-sm sm:text-base">{item.title}</h3>
                    <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
              <div className="border border-orange-500 rounded-xl p-4 sm:p-5 bg-black space-y-2">
                <h3 className="text-white font-bold text-sm sm:text-base">Close</h3>
                <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">Accelerate from pitch to term sheet</p>
              </div>
              <div className="flex flex-wrap gap-3 sm:gap-4 pt-2">
                <button className="px-5 sm:px-6 py-3 bg-orange-500 text-white rounded-lg font-semibold text-sm hover:bg-orange-600 transition">
                  Start Your Free Trial Today
                </button>
                <button
                //   onClick={() => navigate('/reffrence?productId=5')}
                  className="px-5 sm:px-6 py-3 border border-orange-500 text-white rounded-lg font-semibold text-sm hover:bg-orange-500 transition">
                  Explore the Platform
                </button>
              </div>
              {hasRefAccess && (
                <div className="pt-2">
                  <button
                    onClick={() => navigate('/reffrence?productId=5')}
                    className="px-5 sm:px-6 py-3 bg-orange-700 text-white rounded-lg font-semibold text-sm hover:bg-orange-800 transition">
                    Reference Material
                  </button>
                </div>
              )}
            </div>
            <div className="w-full">
              <img src="/Assests/home_page_206.png" alt="Ready to Transform" className="w-full object-cover rounded-xl" style={{ minHeight: '400px', maxHeight: '600px' }} />
            </div>
          </div>
        </div>
      </Section>

    </div>
  );
}